import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Scale, Shield, Heart, CheckCircle2, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default CMS content (matches admin editor defaults)
const defaultContent = {
  hero: {
    title: 'Services &',
    highlight: 'Benefits',
    description: 'Discover the comprehensive support and benefits we provide to our members across Papua New Guinea.',
  },
  intro: {
    heading: 'How We Support Our Members',
    body_markdown: 'PEA PNG provides practical support, representation, and services that help protect members\' rights and wellbeing.',
  },
  services: {
    items: [
      {
        title: 'Industrial Services',
        body_markdown: 'Industrial Services aims to seek redress for financial members with their industrial issues. This includes unlawful terminations, suspensions, and entitlements, among others.',
      },
      {
        title: 'Legal Services',
        body_markdown: 'This is the last resort service we provide to our financial members when their grievances are not addressed administratively. We represent our members in court to seek justice.',
      },
      {
        title: 'Welfare Services',
        body_markdown: 'These services aim to improve the welfare of our financial members through welfare incentives such as life and Medical Insurance and the PEA Credit Scheme.',
      },
    ],
  },
  cta: {
    heading: 'Ready to Join PEA PNG?',
    body_markdown: 'Become a member today and access all these services and benefits',
    button_text: 'Apply for Membership',
    button_link: '/membership',
  },
};

const ServicesPage = () => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/services`);
      const next = { ...defaultContent };
      
      response.data.forEach(item => {
        if (item.section_name && item.content) {
          next[item.section_name] = { ...next[item.section_name], ...item.content };
        }
      });
      
      setContent(next);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const serviceIcons = [
    <Scale className="text-white" size={32} />,
    <Shield className="text-white" size={32} />,
    <Heart className="text-white" size={32} />
  ];

  const serviceColors = [
    'from-orange-50 to-orange-100',
    'from-blue-50 to-blue-100',
    'from-green-50 to-green-100'
  ];

  const iconBgColors = ['bg-orange-500', 'bg-blue-500', 'bg-green-500'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - CMS Driven */}
      <section className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {content.hero.title} <span className="text-orange-500">{content.hero.highlight}</span>
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {content.hero.description}
          </p>
        </div>
      </section>

      {/* Intro Section - CMS Driven */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{content.intro.heading}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {content.intro.body_markdown}
            </p>
          </div>

          {/* Services Cards - CMS Driven */}
          <div className="space-y-8">
            {(content.services.items || []).map((service, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br ${serviceColors[index] || serviceColors[0]} rounded-2xl p-8 shadow-lg card-hover`}
              >
                <div className="flex items-start gap-6">
                  <div className={`flex-shrink-0 w-16 h-16 ${iconBgColors[index] || iconBgColors[0]} rounded-2xl flex items-center justify-center shadow-lg`}>
                    {serviceIcons[index] || serviceIcons[0]}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {service.body_markdown}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Membership Benefits</h2>
            <p className="text-gray-600">
              Enjoy these exclusive benefits as a PEA PNG member
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              'Legal representation in employment disputes',
              'Professional advocacy for your rights',
              'Life and Medical Insurance coverage',
              'Access to PEA Credit Scheme',
              'Support during unlawful terminations',
              'Assistance with entitlements and grievances',
              'Professional development opportunities',
              'Networking with fellow public employees'
            ].map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-white rounded-xl p-6 shadow-md card-hover"
              >
                <CheckCircle2 className="text-orange-500 flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - CMS Driven */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="mx-auto text-white mb-6" size={64} />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {content.cta.heading}
          </h2>
          <p className="text-lg text-orange-100 mb-8">
            {content.cta.body_markdown}
          </p>
          <Link
            to={content.cta.button_link || '/membership'}
            className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-xl font-bold hover:bg-orange-50 transition-all hover:scale-105 shadow-xl"
          >
            {content.cta.button_text || 'Apply for Membership'}
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
